import moment from 'moment';
import momentTimezone from 'moment-timezone';
import {VNode} from 'snabbdom/vnode';
import {jsx} from 'panel';

import {TooltipEl} from 'common/components/tooltip';
import {reporter} from 'common/report/error-reporting';
import {Store} from 'common/report/stores/store';
import {ProjectDetails} from 'common/report/stores/project-details';
import {mpColors} from 'common/util/colors';
import {getCurrentMoment, parseDate} from 'common/util/date';
import {pluralize} from 'common/util/string';
import {CALENDAR_DATE_FORMAT, DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DatePickerMode, getDatePickerMode} from 'common/widgets/date-range-picker-new/util';
import {Placement} from 'common/components/tooltip/types';

function getAbbreviatedTimezoneAsOf(date: moment.Moment, timezone: null | string): null | string {
  try {
    return momentTimezone.tz.zone(timezone).abbr(date.valueOf());
  } catch (err) {
    // report the error but don't die because we couldn't parse a timezone
    reporter.error(err);
    return null;
  }
}

export function renderDateRangeTooltip(dateRange: DateRange, currentProjectStore: Store<ProjectDetails>): VNode {
  const pickerMode = getDatePickerMode(dateRange);
  if (pickerMode === DatePickerMode.Between || pickerMode === DatePickerMode.On) {
    // add +1 to getIntervalDays() because we're showing the full span of the date range, which includes both From & To dates
    const daySpan = dateRange.getIntervalDays() + 1;
    return (
      <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>{`${daySpan} ${pluralize(
        `day`,
        daySpan,
      )}`}</TooltipEl>
    );
  }

  const projectAsOfDate: null | moment.Moment = (() => {
    if (currentProjectStore.value?.projectAsOfDate) {
      const parsedDate = parseDate(currentProjectStore.value.projectAsOfDate);
      if (!parsedDate) {
        throw new Error(`"project-as-of-date" attribute does not contain a valid date string`);
      }
      return moment(parsedDate);
    }
    return null;
  })();
  const {from, to} = dateRange.getCalendarDatesAsOf(
    (projectAsOfDate ?? getCurrentMoment({localized: true})).format(CALENDAR_DATE_FORMAT),
  );
  const fromDate = moment(parseDate(from));
  const toDate = moment(parseDate(to));
  const isSameDate = fromDate.isSame(toDate, `day`);
  return (
    <TooltipEl attrs={{placement: Placement.Top, elref: `dateRangeTooltip`}}>
      <div style={{'text-align': `center`}}>
        <div>
          <span>
            {isSameDate
              ? fromDate.format(`MMM D, YYYY`)
              : `${fromDate.format(`MMM D, YYYY`)} \u2192 ${toDate.format(`MMM D, YYYY`)}`}
          </span>
          &nbsp;
          {currentProjectStore.value ? (
            <span style={{color: mpColors[`$gray3`]}}>{`(${getAbbreviatedTimezoneAsOf(
              toDate,
              currentProjectStore.value.timezone,
            )})`}</span>
          ) : null}
        </div>
        {projectAsOfDate ? <div>Data last imported {projectAsOfDate.format(`MMM D, YYYY`)}</div> : null}
      </div>
    </TooltipEl>
  );
}
